/**
 * 任务配置编辑器组件
 * 提供任务配置的表单式编辑界面
 */
import React, { useState, useEffect } from 'react'
import {
  Card,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Tabs,
  Tag,
  Row,
  Col,
  Divider,
  Switch,
  Typography,
  Tooltip,
  message
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  UpOutlined,
  DownOutlined,
  FlagOutlined,
  CheckCircleOutlined,
  GiftOutlined,
} from '@ant-design/icons'
import type { QuestConfigData, QuestObjective, QuestReward } from '@/types/scriptConfigBlock'

const { TextArea } = Input
const { Text } = Typography
const { TabPane } = Tabs

// 任务编辑器属性
interface QuestEditorFormProps {
  initialData?: QuestConfigData
  onSave: (data: QuestConfigData) => Promise<void>
  loading?: boolean
}

// 目标编辑器
const ObjectiveEditor: React.FC<{
  objective: QuestObjective
  index: number
  onUpdate: (index: number, objective: QuestObjective) => void
  onRemove: (index: number) => void
  onMoveUp: (index: number) => void
  onMoveDown: (index: number) => void
}> = ({ objective, index, onUpdate, onRemove, onMoveUp, onMoveDown }) => {
  return (
    <Card
      size="small"
      title={
        <Space>
          <Tag color={objective.is_optional ? 'orange' : 'blue'}>
            {objective.is_optional ? '可选目标' : '必做目标'}
          </Tag>
          <Text>{objective.description || `目标 #${index + 1}`}</Text>
        </Space>
      }
      extra={
        <Space>
          <Tooltip title="上移">
            <Button size="small" icon={<UpOutlined />} onClick={() => onMoveUp(index)} disabled={index === 0} />
          </Tooltip>
          <Tooltip title="下移">
            <Button size="small" icon={<DownOutlined />} onClick={() => onMoveDown(index)} />
          </Tooltip>
          <Tooltip title="删除">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onRemove(index)} />
          </Tooltip>
        </Space>
      }
      style={{ marginBottom: 8 }}
    >
      <Row gutter={12}>
        <Col span={14}>
          <Form.Item label="目标描述">
            <Input
              value={objective.description}
              onChange={(e) => onUpdate(index, { ...objective, description: e.target.value })}
              placeholder="描述这个目标的具体内容"
            />
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="目标类型">
            <Select
              value={objective.type}
              onChange={(value) => onUpdate(index, { ...objective, type: value })}
              style={{ width: '100%' }}
            >
              <Select.Option value="talk">对话</Select.Option>
              <Select.Option value="collect">收集</Select.Option>
              <Select.Option value="explore">探索</Select.Option>
              <Select.Option value="solve">解谜</Select.Option>
              <Select.Option value="time">限时</Select.Option>
              <Select.Option value="custom">自定义</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={4}>
          <Form.Item label="目标键">
            <Input
              value={objective.target_key || ''}
              onChange={(e) => onUpdate(index, { ...objective, target_key: e.target.value })}
              placeholder="关联的对象键"
            />
          </Form.Item>
        </Col>
        <Col span={2}>
          <Form.Item label="可选">
            <Switch
              checked={objective.is_optional}
              onChange={(checked) => onUpdate(index, { ...objective, is_optional: checked })}
              checkedChildren="是"
              unCheckedChildren="否"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={6}>
          <Form.Item label="需求数量">
            <InputNumber
              value={objective.required_count}
              onChange={(value) => onUpdate(index, { ...objective, required_count: value ?? undefined })}
              min={1}
              style={{ width: '100%' }}
              placeholder="1"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="提示信息">
            <Input
              value={objective.hint || ''}
              onChange={(e) => onUpdate(index, { ...objective, hint: e.target.value })}
              placeholder="给玩家的提示"
            />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  )
}

// 奖励编辑器
const RewardEditor: React.FC<{
  reward: QuestReward
  index: number
  onUpdate: (index: number, reward: QuestReward) => void
  onRemove: (index: number) => void
}> = ({ reward, index, onUpdate, onRemove }) => {
  return (
    <Row gutter={8} style={{ marginBottom: 8 }}>
      <Col span={4}>
        <Select
          value={reward.type}
          onChange={(value) => onUpdate(index, { ...reward, type: value })}
          style={{ width: '100%' }}
        >
          <Select.Option value="item">物品</Select.Option>
          <Select.Option value="points">积分</Select.Option>
          <Select.Option value="experience">经验</Select.Option>
          <Select.Option value="skill">技能</Select.Option>
          <Select.Option value="unlock">解锁</Select.Option>
          <Select.Option value="story">剧情</Select.Option>
        </Select>
      </Col>
      <Col span={4}>
        <Input
          value={reward.key || ''}
          onChange={(e) => onUpdate(index, { ...reward, key: e.target.value })}
          placeholder="物品/技能键"
        />
      </Col>
      <Col span={8}>
        <Input
          value={reward.value !== undefined ? String(reward.value) : ''}
          onChange={(e) => {
            const val = e.target.value
            onUpdate(index, { ...reward, value: val })
          }}
          placeholder="数量或值"
        />
      </Col>
      <Col span={6}>
        <Input
          value={reward.description || ''}
          onChange={(e) => onUpdate(index, { ...reward, description: e.target.value })}
          placeholder="奖励描述"
        />
      </Col>
      <Col span={2}>
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onRemove(index)} />
      </Col>
    </Row>
  )
}

// 主组件
const QuestEditorForm: React.FC<QuestEditorFormProps> = ({
  initialData,
  onSave,
  loading = false
}) => {
  const [form] = Form.useForm()
  const [objectives, setObjectives] = useState<QuestObjective[]>([])
  const [rewards, setRewards] = useState<QuestReward[]>([])
  const [prerequisites, setPrerequisites] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState('basic')
  const [saving, setSaving] = useState(false)

  // 初始化表单数据
  useEffect(() => {
    if (initialData) {
      // 处理条件数组：确保元素是字符串再 join
      const normalizeConditions = (conditions: any): string => {
        if (!Array.isArray(conditions)) return ''
        return conditions.map(c => String(c)).join(', ')
      }

      form.setFieldsValue({
        key: initialData.key,
        name: initialData.name,
        description: initialData.description,
        quest_type: initialData.quest_type,
        difficulty: initialData.difficulty,
        time_limit: initialData.time_limit,
        auto_advance: initialData.auto_advance,
        success_conditions: normalizeConditions(initialData.success_conditions),
        failure_conditions: normalizeConditions(initialData.failure_conditions),
      })
      setObjectives(initialData.objectives || [])
      setRewards(initialData.rewards || [])
      setPrerequisites(initialData.prerequisites || [])
    } else {
      form.resetFields()
      setObjectives([])
      setRewards([])
      setPrerequisites([])
    }
  }, [initialData, form])

  // 保存处理
  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)

      const questData: QuestConfigData = {
        key: values.key,
        name: values.name,
        description: values.description,
        quest_type: values.quest_type,
        difficulty: values.difficulty,
        objectives,
        rewards,
        prerequisites,
        time_limit: values.time_limit,
        auto_advance: values.auto_advance,
        success_conditions: values.success_conditions ? values.success_conditions.split(',').map((s: string) => s.trim()) : [],
        failure_conditions: values.failure_conditions ? values.failure_conditions.split(',').map((s: string) => s.trim()) : [],
      }

      await onSave(questData)
    } catch (error: any) {
      if (error?.errorFields) {
        message.error('请填写必填项')
      } else {
        console.error('表单验证失败:', error)
      }
    } finally {
      setSaving(false)
    }
  }

  // 目标操作
  const addObjective = () => {
    setObjectives([
      ...objectives,
      {
        id: `obj_${Date.now()}`,
        description: '',
        type: 'talk',
        required_count: 1,
        is_optional: false,
      }
    ])
  }

  const updateObjective = (index: number, objective: QuestObjective) => {
    const newObjectives = [...objectives]
    newObjectives[index] = objective
    setObjectives(newObjectives)
  }

  const removeObjective = (index: number) => {
    setObjectives(objectives.filter((_, i) => i !== index))
  }

  const moveObjective = (index: number, direction: 'up' | 'down') => {
    const newObjectives = [...objectives]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex >= 0 && targetIndex < objectives.length) {
      [newObjectives[index], newObjectives[targetIndex]] = [newObjectives[targetIndex], newObjectives[index]]
      setObjectives(newObjectives)
    }
  }

  // 奖励操作
  const addReward = () => {
    setRewards([
      ...rewards,
      { type: 'item', value: 1 }
    ])
  }

  const updateReward = (index: number, reward: QuestReward) => {
    const newRewards = [...rewards]
    newRewards[index] = reward
    setRewards(newRewards)
  }

  const removeReward = (index: number) => {
    setRewards(rewards.filter((_, i) => i !== index))
  }

  return (
    <Card
      title={
        <Space>
          <FlagOutlined style={{ color: '#52c41a' }} />
          <span>{initialData?.name || '新建任务'}</span>
          {initialData?.key && <Text code style={{ fontSize: 12 }}>{initialData.key}</Text>}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={handleSave}
          loading={saving || loading}
        >
          保存
        </Button>
      }
    >
      <Form form={form} layout="vertical">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="基本信息" key="basic">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="key" label="任务键" rules={[{ required: true }]}>
                  <Input placeholder="例如: investigate_mystery" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="name" label="任务名称" rules={[{ required: true }]}>
                  <Input placeholder="例如: 调查神秘事件" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="quest_type" label="任务类型" rules={[{ required: true }]}>
                  <Select placeholder="选择任务类型">
                    <Select.Option value="main">主线任务</Select.Option>
                    <Select.Option value="branch">支线任务</Select.Option>
                    <Select.Option value="daily">日常任务</Select.Option>
                    <Select.Option value="event">活动任务</Select.Option>
                    <Select.Option value="hidden">隐藏任务</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="difficulty" label="难度等级">
                  <Select placeholder="选择难度">
                    <Select.Option value="easy">简单</Select.Option>
                    <Select.Option value="normal">普通</Select.Option>
                    <Select.Option value="hard">困难</Select.Option>
                    <Select.Option value="expert">专家</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="time_limit" label="时间限制（分钟）">
                  <InputNumber min={0} placeholder="0 表示无限制" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="auto_advance" label="自动推进">
                  <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="description" label="任务描述">
              <TextArea rows={3} placeholder="描述任务背景和目标..." />
            </Form.Item>

            <Form.Item label="前置任务">
              <Select
                mode="tags"
                value={prerequisites}
                onChange={setPrerequisites}
                placeholder="输入前置任务键后按回车"
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="success_conditions" label="成功条件（逗号分隔）">
                  <Input placeholder="例如: var_evidence_collected >= 3, npc_stranger_friendly > 50" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="failure_conditions" label="失败条件（逗号分隔）">
                  <Input placeholder="例如: time_elapsed > 30" />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab={`目标 (${objectives.length})`} key="objectives">
            <div style={{ marginBottom: 16 }}>
              <Button type="dashed" icon={<PlusOutlined />} onClick={addObjective}>
                添加目标
              </Button>
            </div>

            {objectives.map((objective, index) => (
              <ObjectiveEditor
                key={objective.id || index}
                objective={objective}
                index={index}
                onUpdate={updateObjective}
                onRemove={removeObjective}
                onMoveUp={(i) => moveObjective(i, 'up')}
                onMoveDown={(i) => moveObjective(i, 'down')}
              />
            ))}

            {objectives.length === 0 && (
              <Card size="small" style={{ textAlign: 'center' }}>
                <Text type="secondary">暂无目标，点击上方按钮添加</Text>
              </Card>
            )}
          </TabPane>

          <TabPane tab={`奖励 (${rewards.length})`} key="rewards">
            <div style={{ marginBottom: 16 }}>
              <Button type="dashed" icon={<GiftOutlined />} onClick={addReward}>
                添加奖励
              </Button>
            </div>

            <Row gutter={8} style={{ marginBottom: 8 }}>
              <Col span={4}><Text strong>类型</Text></Col>
              <Col span={4}><Text strong>物品键</Text></Col>
              <Col span={8}><Text strong>数值</Text></Col>
              <Col span={6}><Text strong>描述</Text></Col>
              <Col span={2}></Col>
            </Row>

            {rewards.map((reward, index) => (
              <RewardEditor
                key={index}
                reward={reward}
                index={index}
                onUpdate={updateReward}
                onRemove={removeReward}
              />
            ))}

            {rewards.length === 0 && (
              <Card size="small" style={{ textAlign: 'center' }}>
                <Text type="secondary">暂无奖励配置，点击上方按钮添加</Text>
              </Card>
            )}
          </TabPane>

          <TabPane tab="完成逻辑" key="logic">
            <Card title="成功条件" style={{ marginBottom: 16 }}>
              <Text type="secondary">
                定义任务成功的条件，支持变量比较、状态检查等。
              </Text>
              <Divider />
              <ul>
                <li><Text code>var_evidence {'>='} 3</Text> - 变量值判断</li>
                <li><Text code>quest.main_step2.completed</Text> - 任务状态检查</li>
                <li><Text code>npc_stranger_friendly {'>'} 50</Text> - NPC 关系值</li>
                <li><Text code>player.inventory.has</Text> - 背包检查</li>
              </ul>
            </Card>

            <Card title="失败条件">
              <Text type="secondary">
                定义任务失败的条件，超时、死亡、放弃等情况。
              </Text>
              <Divider />
              <ul>
                <li><Text code>time_elapsed {'>'} 30</Text> - 超时（分钟）</li>
                <li><Text code>player.health {'<='} 0</Text> - 玩家死亡</li>
                <li><Text code>quest.abandoned</Text> - 主动放弃</li>
              </ul>
            </Card>

            <Card title="自动推进" style={{ marginTop: 16 }}>
              <Space direction="vertical">
                <Switch checkedChildren="开启" unCheckedChildren="关闭" /> 开启后，当所有必做目标完成后自动推进到下一阶段
              </Space>
            </Card>
          </TabPane>
        </Tabs>
      </Form>
    </Card>
  )
}

export default QuestEditorForm
